import { Alert, Anchor, Text } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useDebouncedValue } from '@mantine/hooks'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { phoneNumbers, type ResolvedField, type Resource } from '@tnn/core'
import { useLinkComponent } from '@tnn/ui'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { type Rec, resourceApi } from '../api/resources'

const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

/**
 * Under a `warnTaken` field: the records that already hold what was typed (a tax ID, a phone), each a link to open
 * before adding the same partner twice. Only once the value is whole (a tax ID's 13 digits, a full phone number), and
 * changed: a record that already had it says nothing.
 */
export function TakenWarning({
  resource,
  field,
  form,
  record,
  path,
}: {
  resource: Resource
  field: ResolvedField
  form: UseFormReturnType<Record<string, unknown>>
  record?: Rec
  path: string
}) {
  const { t } = useTranslation()
  const Link = useLinkComponent()
  const others = field.warnTaken?.with ?? []
  const value = text(form.values[field.name])
  const alongside = Object.fromEntries(others.map((name) => [name, text(form.values[name])]).filter(([, v]) => v))
  const changed = !record || [field.name, ...others].some((name) => form.isDirty(name))
  const whole =
    field.type === 'phone' ? phoneNumbers(value).length > 0 : !!value && resource.schema.shape[field.name]?.safeParse(value).success
  const [asked] = useDebouncedValue(changed && whole ? JSON.stringify([value, alongside]) : '', 400)
  const found = useQuery({
    queryKey: ['taken', resource.key, field.name, asked, record?.id],
    queryFn: () => {
      const [v, w] = JSON.parse(asked) as [string, Record<string, string>]
      return resourceApi(resource.key).taken(field.name, v, w, record?.id)
    },
    enabled: !!asked,
    staleTime: 30_000,
  })
  const items = asked && found.data ? found.data.items : []
  if (!items.length) return null
  const more = (found.data?.total ?? 0) - items.length
  return (
    <Alert variant="light" color="warning" radius="md" icon={<IconAlertTriangle size={18} />} p="sm" mt={8}>
      <Text size="sm">
        {t('form.taken', { field: t(field.label) })}{' '}
        {items.map((item, i) => (
          <Fragment key={item.value}>
            {i > 0 && ', '}
            <Anchor component={Link} href={`/${path}/${item.value}`} size="sm">
              {item.label}
            </Anchor>
          </Fragment>
        ))}
        {more > 0 && ` ${t('form.takenMore', { count: more })}`}
      </Text>
    </Alert>
  )
}
